import { useCallback, useEffect, useState } from 'react'
import type { ApiFailure, MaintenanceStatus } from '../api/types'
import { ApiError } from '../api/types'
import type { Api } from '../api'
import { Button, Card, CardHeader, Field, Loading, SearchSelect, inputClass, relativeTime } from '../components/ui'
import { FailureState } from '../components/FailureState'
import { REPORT_PAGES } from '../components/Shell'

/**
 * DATA REPORTS, not pages and not admin modules.
 *
 * Taking "Receivables" offline takes all of its pages offline — the aging
 * matrix, DSO, the decomposition trees, the lot. That is what an operator
 * means when they take a report down, and it is the granularity the platform
 * isolates at: another report keeps serving throughout.
 *
 * Only the data reports belong here. "Access" (the permissions module) is an
 * administration surface, not a report — the sidebar files it under
 * Administration for the same reason — so it is deliberately excluded.
 *
 * DERIVED FROM NAV, not listed again. This was a hand-kept copy until
 * 8 Sep 2026, when it was found still naming two reports the day after a third
 * shipped — so Material Planning could not be taken offline at all. See
 * REPORT_PAGES in Shell.tsx for why NAV is the list that cannot go stale.
 */
const REPORTS = REPORT_PAGES

/**
 * Requirement R6: put a single report, or the whole platform, into maintenance.
 *
 * The form is shaped by the anti-lockout rules the database enforces, so the UI
 * cannot offer something the procedure will reject:
 *   * duration is a DROPDOWN with a 7-day maximum, never a free-text date. An
 *     absolute timestamp invites a timezone mistake, and a timezone mistake here
 *     means either an instantly-expired window or one that runs hours long.
 *   * a reason is required, because it is what tells the next admin whether it
 *     is safe to lift.
 *   * scope is a picklist of real reports, so a typo cannot create a window that
 *     appears to work and protects nothing.
 */
export function MaintenancePage({ api }: { api: Api }) {
  const [status, setStatus] = useState<MaintenanceStatus | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  // A failed STATUS READ is kept apart from a failed action. Sharing `failure`
  // left "Current state" on "Checking…" forever, hid every open window and its
  // Lift button, and printed the read's error under the form as if a change had
  // been refused — during an incident, when this page matters most.
  const [statusFailure, setStatusFailure] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)

  const [scope, setScope] = useState('platform')
  const [reason, setReason] = useState('')
  const [message, setMessage] = useState('')
  const [durationMinutes, setDuration] = useState(60)
  const [allowReadGrace, setReadGrace] = useState(false)

  const refresh = useCallback(() => {
    setStatusFailure(null)
    // The action error too, as it always did. refresh() runs on mount, after a
    // SUCCESSFUL action and from the header Refresh button — never after a
    // failed action — so clearing it here only ever dismisses a stale message.
    setFailure(null)
    api
      .maintenanceStatus()
      .then(setStatus)
      .catch((e: unknown) =>
        setStatusFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' }),
      )
  }, [api])

  useEffect(refresh, [refresh])

  async function submit() {
    setBusy(true)
    setFailure(null)
    try {
      await api.setMaintenance({ scope, reason, message: message || undefined, durationMinutes, allowReadGrace })
      setReason('')
      setMessage('')
      refresh()
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not open the window.' })
    } finally {
      setBusy(false)
    }
  }

  async function clear(target: string) {
    setBusy(true)
    // Cleared up front, as submit() does. This used to happen by accident —
    // refresh() wiped the shared `failure` — and when the status read got its
    // own state that accident stopped: a failed Lift followed by a successful
    // one left the red "Could not clear the window." standing beside a window
    // that had just been lifted.
    setFailure(null)
    try {
      await api.clearMaintenance(target)
      refresh()
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not clear the window.' })
    } finally {
      setBusy(false)
    }
  }

  // A forbidden/no-permission failure — from the read or from an action —
  // replaces the page; any other failure does not.
  const refusal = [statusFailure, failure].find(
    (f) => f !== null && (f.kind === 'forbidden' || f.kind === 'no-permissions'),
  )
  if (refusal) {
    return <FailureState failure={refusal} />
  }

  return (
    <div className="space-y-5">
      <header>
        <div className="eyebrow">Operations</div>
        <h1 className="mt-1 text-(length:--text-xl) font-semibold tracking-[-0.015em] text-(--color-ink)">
          Maintenance
        </h1>
        {/* WHAT THE USER SEES IS STATED HERE, not left to be discovered.
            An operator choosing between "platform" and one report is choosing
            between two quite different experiences for a couple of thousand
            people, and until 17 Sep 2026 the screen described neither — it
            described the window. */}
        <p className="mt-1.5 max-w-2xl text-(length:--text-base) leading-relaxed text-(--color-ink-3)">
          Take one report, or the whole platform, offline. A platform window closes the front door:
          people who sign in get a maintenance screen instead of MBI. A report window closes that
          report only — it stays in the sidebar, marked, and opening it says so. Every window expires
          on its own, so nothing here can leave the platform down because somebody forgot to come back.
        </p>
      </header>

      <Card>
        <CardHeader title="Current state" right={<Button onClick={refresh}>Refresh</Button>} />
        {/* EVERY open window, each with its own Lift.
            This rendered a single `window` from a read that only ever returned
            platform-scoped ones, so a window opened on a report was invisible —
            and with nothing on screen there was no Lift button, which made it
            uncancellable too. It could only expire on its own. */}
        {/* A failed read is said HERE, where the state would be, with its
            reference and a retry.

            BUT IT DOES NOT TAKE THE LIFT BUTTONS WITH IT. If an earlier read
            succeeded, its windows stay listed beneath the failure, marked as
            the last successful read. The comment above records what happens
            when a Lift button is not on screen — the window becomes
            uncancellable — and a failed re-read during an incident is exactly
            when an administrator most needs to lift one. Only when nothing has
            ever been read does the failure stand alone. */}
        {statusFailure && <FailureState failure={statusFailure} onRetry={refresh} />}
        {statusFailure && status !== null && (
          <p className="mt-3 text-(length:--text-small) text-(--color-ink-3)">
            Showing the last successful read. It may be out of date.
          </p>
        )}
        {status === null ? (
          statusFailure ? null : <Loading label="Checking…" />
        ) : status.windows.length > 0 ? (
          <ul className="divide-y divide-(--color-line)">
            {status.windows.map((w) => (
              <li key={w.scope} className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span aria-hidden className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-(--color-warn) ring-4 ring-(--color-warn)/15" />
                    <span className="text-(length:--text-lg) font-semibold text-(--color-ink)">
                      {w.scope === 'platform'
                        ? 'The whole platform is in maintenance'
                        : `${REPORTS.find((r) => `report:${r.slug}` === w.scope)?.name ?? w.scope.replace('report:', '')} is in maintenance`}
                    </span>
                  </div>
                  <p className="mt-1.5 text-(length:--text-body) leading-relaxed text-(--color-ink-2)">{w.message}</p>
                  <p className="mt-1 text-(length:--text-small) text-(--color-ink-3)">
                    Lifts on its own in about {relativeTime(w.expectedEndUtc)} ·{' '}
                    {/* The consequence, per open window, in the operator's own
                        console. "Reads are still being served" was true and did
                        not answer the question anybody standing here is asking,
                        which is what the people on the other end are looking at
                        right now. */}
                    {w.allowReadGrace
                      ? 'still open to everyone, with a notice'
                      : w.scope === 'platform'
                        ? 'nobody but System Admins can get in'
                        : 'everyone but System Admins sees a closed screen'}
                  </p>
                </div>
                <Button variant="danger" disabled={busy} onClick={() => void clear(w.scope)}>
                  Lift now
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex items-center gap-2.5 px-5 py-4 text-(length:--text-body) text-(--color-ink-2)">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-(--color-good) ring-4 ring-(--color-good)/15"
            />
            Everything is serving normally.
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title="Open a window" subtitle="Bounded at 7 days — a window with no end is how people get locked out." />
        <div className="grid gap-4 px-5 py-4 @2xl:grid-cols-2">
          {/* SearchSelect for consistency with every other dropdown; with
              lists this short it opens straight to the options — the search
              box appears only past its threshold. */}
          <Field label="Scope">
            <SearchSelect
              label="Scope"
              value={scope}
              onChange={setScope}
              options={[
                { value: 'platform', label: 'The whole platform' },
                ...REPORTS.map((r) => ({ value: `report:${r.slug}`, label: r.name })),
              ]}
            />
          </Field>

          <Field label="Duration">
            <SearchSelect
              label="Duration"
              value={String(durationMinutes)}
              onChange={(v) => setDuration(Number(v))}
              options={[
                { value: '15', label: '15 minutes' },
                { value: '60', label: '1 hour' },
                { value: '240', label: '4 hours' },
                { value: '720', label: '12 hours' },
                { value: '1440', label: '1 day' },
                { value: '10080', label: '7 days', hint: 'maximum' },
              ]}
            />
          </Field>

          <Field label="Reason" hint="Internal. Tells the next admin whether it is safe to lift.">
            <input
              className={inputClass}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Schema migration 004"
            />
          </Field>

          <Field label="Message to users" hint="Shown on the maintenance screen. Leave blank for the default.">
            <input
              className={inputClass}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Back shortly after a scheduled upgrade."
            />
          </Field>

          <label className="flex items-start gap-2.5 rounded-md border border-(--color-line) bg-(--color-surface-2) px-3.5 py-3 text-(length:--text-body) text-(--color-ink-2) @2xl:col-span-2">
            <input
              type="checkbox"
              className="mt-1 accent-(--color-navy-700)"
              checked={allowReadGrace}
              onChange={(e) => setReadGrace(e.target.checked)}
            />
            <span>
              Keep serving reads
              {/* Spelled out because this box is the difference between a
                  closed screen and a working one, and the old wording —
                  "viewing continues" — did not say that the maintenance screen
                  therefore never appears. An operator who ticks it expecting a
                  gentler outage gets no outage at all. */}
              <span className="mt-0.5 block text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
                No maintenance screen: the report keeps opening and the figures keep arriving, with a
                strip across the top saying a window is open. Anything that writes is still refused.
                For changes where stale reads are harmless. Off by default, and off is what closes it.
              </span>
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-3 @2xl:col-span-2">
            <Button variant="primary" disabled={busy || !reason.trim()} onClick={() => void submit()}>
              {busy ? 'Working…' : 'Open maintenance window'}
            </Button>
            {!reason.trim() && <span className="text-(length:--text-small) text-(--color-ink-3)">A reason is required.</span>}
          </div>

          {failure && (
            <div className="rounded-md border border-(--color-overdue)/40 bg-(--color-overdue)/8 px-3.5 py-2.5 text-(length:--text-body) text-(--color-overdue) @2xl:col-span-2">
              {/* forbidden/no-permissions replaced the whole page above, and a
                  failed status read has its own place in Current state, so what
                  reaches here is an action failure: show it inline and keep the
                  form filled in rather than discarding what was typed. */}
              {'title' in failure ? failure.title : 'Could not apply the change.'}
            </div>
          )}
        </div>
      </Card>

      <p className="max-w-2xl text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
        You will keep working during a window you open — System Admins pass through, and this page is never
        gated by maintenance, so it is always possible to lift one. That also means you cannot see what
        you have done from here: use <span className="font-medium text-(--color-ink-2)">Sign in as someone else</span>
        to look at it through their eyes, which is the only way to see what you have done.
      </p>
    </div>
  )
}
